import { supabase } from '../../../foundation/altzero_base/base/utils/supabaseClient';
import type { ApiResponse, BvSiteStatus } from '../types/billValidationTypes';
import type { ShadowBillSection } from '../types/billTypes';
import type { DailyIntakeRow } from '../lib/dailyIntake';
import type { GapReport } from '../lib/gapCheck';
import { validateInvoicePdf } from '../utils/invoiceDrop';

const API_BASE = (import.meta.env.VITE_API_URL ?? 'http://localhost:3001') + '/api/bill-validation/email';

export type BillEmailAccount = {
  id: string;
  email: string;
  is_active: boolean;
  created_at: string;
  /**
   * Whether the inbox sync fetches from this mailbox. false = send-only: it
   * still appears in the report send dialog, but nothing is read from it — and
   * replies to it are therefore not monitored.
   */
  poll_inbox?: boolean;
};

export type BillEmailConfig = {
  org_id: string;
  // Trusted sender domains — tagging only, never used to exclude an email from ingestion.
  sender_domains: string[];
  poll_interval_hours: number;
  last_polled_at: string | null;
};

export type InboxStatus = 'unmatched' | 'matched_auto' | 'matched_manual' | 'ignored';

// A consolidated invoice splits into one section per site (Phase 2). Stored on
// bv_invoice_email.detected_sections at ingest; empty for single-site invoices.
export type DetectedSection = {
  nmi: string;
  period_from: string | null;
  period_to: string | null;
};

// One site's slice of a consolidated invoice, as sent to the assign endpoint.
export type AssignSection = {
  nmi: string | null;
  site_id: string | null;
  bill_from: string | null;
  bill_to: string | null;
};

export type AssignSectionResult = {
  data: {
    bill_id: string;
    parsingAttachments: number;
    /** False when the assignment stood but the parse could not be queued; `warning` says what happens next. */
    parseQueued?: boolean;
    warning?: string;
    sections: { nmi: string | null; site_id: string; bill_id: string; periodMode: 'matched' | 'created' }[];
    unresolved: { nmi: string | null; reason: string }[];
  };
};

/** Why an ingested invoice was held for a person instead of finishing itself. */
export type HeldReason =
  | 'nmi'
  | 'multiple_nmis'
  | 'site'
  | 'period'
  | 'retailer'
  | 'not_enrolled';

/** Why bulk assign left an auto-matched email for a person. */
export type BulkAssignSkip = 'no_site' | 'no_period' | 'multiple_nmis' | 'held' | 'not_enrolled' | 'tender_site';

/**
 * The identity pass's result, stored on the email at ingest.
 *
 * Every field is nullable because the whole point is that some of them are
 * missing — that is what `held_for` lists, and what the person opening the
 * drawer is being asked to supply.
 */
export type AiExtraction = {
  nmi: string | null;
  nmis: string[] | null;
  period_from: string | null;
  period_to: string | null;
  /** The retailer name as printed on the invoice. */
  retailer_name: string | null;
  /** The retailer row it matched, when it matched exactly one. */
  retailer_id: string | null;
  retailer_match: 'exact' | 'none' | 'ambiguous' | null;
  account_number: string | null;
  invoice_number: string | null;
  invoice_date: string | null;
  total: number | null;
  /** True when the email assigned itself to a bill and started parsing. */
  auto_proceeded: boolean | null;
  held_for: HeldReason[] | null;
  held_reason: string | null;
  extracted_at: string | null;
};

/** What became of one PDF dropped into the inbox. */
export type InboxUploadResult = {
  status: 'ingested' | 'duplicate' | 'filtered';
  emailId: string | null;
  reason: string | null;
  /** Where it landed: unmatched, auto-matched to a site, or assigned and parsing. */
  inboxStatus: InboxStatus | null;
  assigned: boolean;
};

export type InboxEmail = {
  id: string;
  org_id: string;
  /** Null for a PDF dropped into the inbox (source 'upload'). */
  account_id: string | null;
  /** 'zip': one PDF out of a zip a retailer emailed, an item of its own. */
  source?: 'gmail' | 'upload' | 'zip';
  sender_email: string;
  sender_name: string | null;
  subject: string | null;
  body_snippet: string | null;
  received_at: string;
  status: InboxStatus;
  matched_by: 'auto' | 'manual' | null;
  /** When the email was attached to a bill — job 68 uses it to spot a parse that never started. */
  assigned_at?: string | null;
  site_id: string | null;
  bill_id: string | null;
  detected_nmis?: string[] | null;
  detected_period_from?: string | null;
  detected_period_to?: string | null;
  detected_sections?: DetectedSection[] | null;
  /**
   * What the AI read off the invoice at ingest, and why the email did or did
   * not assign and parse itself. Null for emails ingested before that existed.
   */
  ai_extraction?: AiExtraction | null;
  // bv_extn carries the site's bill-validation enrolment (status + enrolment /
  // unenrolment dates) so the inbox can say whether an auto-matched site is
  // actually Enrolled without a second fetch per row (job 19).
  site?: {
    id: string;
    name?: string;
    nmi?: string;
    bv_extn?: { bill_val_status?: BvSiteStatus; enrolment_date?: string | null; unenrolment_date?: string | null } | null;
  } | null;
  bill?: { id: string; bill_from: string; bill_to: string } | null;
  attachments?: { id: string; parsing_status: InboxAttachment['parsing_status']; parse_error: string | null; updated_at?: string | null }[];
};

export type InboxAttachment = {
  id: string;
  email_id: string;
  filename: string;
  mime_type: string | null;
  size_bytes: number | null;
  parsing_status: 'pending' | 'downloading' | 'parsing' | 'parsed' | 'failed';
  parse_error: string | null;
  bill_id: string | null;
};

export type EmailSuggestion = {
  account_id: string;
  gmail_message_id: string;
  subject: string;
  sender_email: string;
  sender_name: string | null;
  received_at: string;
  already_ingested: boolean;
};

export type GmailPreviewAttachment = {
  gmail_attachment_id: string;
  filename: string;
  mime_type: string | null;
  size_bytes: number | null;
};

// counting = Gmail is being asked how many messages the window holds (no total
// yet); running = those messages are being ingested one by one.
export type SyncJobState = 'counting' | 'running' | 'done' | 'error';

export type SyncJob = {
  id: string;
  org_id: string;
  kind: 'manual' | 'backfill';
  state: SyncJobState;
  /** Human-readable window, e.g. "14–16 Jul 2026" or "Last 20 days". */
  label: string;
  total: number | null;
  processed: number;
  ingested: number;
  skipped: number;
  filtered: number;
  /** Messages that failed outright — counted so the tallies add up to `total`. */
  errored: number;
  accounts_synced: number;
  /** A cap stopped the sweep short of every Gmail match. */
  truncated: boolean;
  error: string | null;
  started_at: string;
  /** Last sign of life from the server running the sync. */
  heartbeat_at: string;
  finished_at: string | null;
};

export type GmailPreview = {
  subject: string;
  sender_email: string;
  sender_name: string | null;
  received_at: string;
  bodyHtml: string | null;
  bodyText: string | null;
  attachments: GmailPreviewAttachment[];
};

async function authHeaders(): Promise<Record<string, string>> {
  const { data: { session } } = await supabase.auth.getSession();
  return {
    'Content-Type': 'application/json',
    'x-api-key': 'scopingai',
    ...(session?.user?.id ? { 'x-user-id': session.user.id } : {}),
    ...(session?.access_token ? { Authorization: `Bearer ${session.access_token}` } : {}),
  };
}

// Backend always replies JSON, even on its own 4xx/5xx — but a request can also die
// upstream of our Express handler (gateway timeout, crashed dyno, proxy error page),
// which comes back as plain text/HTML. res.json() would throw a raw JSON.parse error
// in that case ("Unexpected token 'A', "An error o"... is not valid JSON") and that
// cryptic parser message would end up shown to the user as-is. Parse as text first so
// we can turn that into a normal "request failed" message instead.
async function parseJsonResponse(res: Response): Promise<{ json: any; parseError: string | null }> {
  const text = await res.text();
  try {
    return { json: text ? JSON.parse(text) : {}, parseError: null };
  } catch {
    const snippet = text.slice(0, 100).replace(/\s+/g, ' ').trim();
    return { json: null, parseError: `Request failed (${res.status})${snippet ? ` — ${snippet}` : ''}` };
  }
}

async function request<T>(path: string, init?: RequestInit): Promise<ApiResponse<T>> {
  try {
    const headers = await authHeaders();
    const res = await fetch(`${API_BASE}${path}`, { ...init, headers: { ...headers, ...(init?.headers ?? {}) } });
    const { json, parseError } = await parseJsonResponse(res);
    if (parseError) return { success: false, error: parseError };
    if (!res.ok || !json.success) return { success: false, error: json.error ?? `Request failed (${res.status})` };
    return { success: true, data: json.data as T };
  } catch (error: unknown) {
    return { success: false, error: (error as Error).message };
  }
}

export const billEmailService = {
  connect: async (returnTo: string): Promise<ApiResponse<{ url: string }>> =>
    request('/connect', { method: 'POST', body: JSON.stringify({ returnTo }) }),

  listAccounts: async (): Promise<ApiResponse<BillEmailAccount[]>> => request('/accounts'),

  disconnectAccount: async (accountId: string): Promise<ApiResponse<null>> =>
    request(`/accounts/${accountId}/disconnect`, { method: 'POST' }),

  /** Switch a mailbox between send-and-receive and send-only. */
  setPollInbox: async (accountId: string, pollInbox: boolean): Promise<ApiResponse<BillEmailAccount>> =>
    request(`/accounts/${accountId}/poll-inbox`, {
      method: 'POST',
      body: JSON.stringify({ poll_inbox: pollInbox }),
    }),

  getConfig: async (): Promise<ApiResponse<BillEmailConfig>> => request('/config'),

  updateConfig: async (patch: Partial<Pick<BillEmailConfig, 'sender_domains' | 'poll_interval_hours'>>): Promise<ApiResponse<BillEmailConfig>> =>
    request('/config', { method: 'PUT', body: JSON.stringify(patch) }),

  // Returns a job to poll rather than final counts — the sync itself can run for
  // minutes. job_id is null only when the org has no pollable mailbox.
  syncNow: async (range?: { days?: number; from?: string; to?: string }): Promise<
    ApiResponse<{ job_id: string | null; already_running: boolean; accountsSynced: number }>
  > => request('/sync', { method: 'POST', body: JSON.stringify(range ?? {}) }),

  /** The org's in-flight sync, or null. Called on page load to re-attach the progress card after a reload. */
  getActiveSyncJob: async (): Promise<ApiResponse<SyncJob | null>> => request('/inbox/sync-jobs/active'),

  /**
   * Progress for a running sync. Jobs are stored server-side, so a run that was
   * killed comes back as state 'error' rather than vanishing. `gone` (404) now
   * only means an unknown id; callers stop polling and refresh the list.
   */
  getSyncJob: async (jobId: string): Promise<ApiResponse<SyncJob> & { gone?: boolean }> => {
    try {
      const headers = await authHeaders();
      const res = await fetch(`${API_BASE}/inbox/sync-jobs/${jobId}`, { headers });
      if (res.status === 404) return { success: false, gone: true, error: 'Sync job no longer available' };
      const { json, parseError } = await parseJsonResponse(res);
      if (parseError) return { success: false, error: parseError };
      if (!res.ok || !json.success) return { success: false, error: json.error ?? `Request failed (${res.status})` };
      return { success: true, data: json.data as SyncJob };
    } catch (error: unknown) {
      return { success: false, error: (error as Error).message };
    }
  },

  /** Per-UTC-day intake: found in Gmail, stored, outcome, PDFs parsed. Newest first. */
  getDailyIntake: async (days = 30): Promise<ApiResponse<{ rows: DailyIntakeRow[] }>> =>
    request(`/inbox/daily?days=${days}`),

  /** Read-only per-UTC-day Gmail vs stored check, at most 31 days. */
  checkGaps: async (from: string, to: string): Promise<ApiResponse<GapReport>> =>
    request(`/inbox/gaps?from=${encodeURIComponent(from)}&to=${encodeURIComponent(to)}`),

  /** Admin only: move the automatic sync's watermark back to `from` (YYYY-MM-DD). */
  rescanFrom: async (from: string): Promise<ApiResponse<{ oldWatermark: string; newWatermark: string }>> =>
    request('/inbox/rescan', { method: 'POST', body: JSON.stringify({ from }) }),

  getInboxCounts: async (): Promise<ApiResponse<{ all: number; unmatched: number; matched_auto: number; matched_manual: number; ignored: number }>> =>
    request('/inbox/counts'),

  /**
   * Per-`parsing_status` attachment counts for the Parse Status chips (job 75).
   *
   * Counts ATTACHMENTS, not emails — parse state belongs to the file and one
   * email can carry several. There is deliberately no 'stalled' key: that state
   * is derived from elapsed time by utils/parseState, so a server-side figure
   * would be a second implementation of the rule and would disagree with the
   * badges. Stalled rows are counted under 'parsing' or 'pending', whichever
   * they actually hold.
   */
  getParseCounts: async (params: { from?: string; to?: string } = {}): Promise<
    ApiResponse<Record<string, number>>
  > => {
    const qs = new URLSearchParams();
    if (params.from) qs.set('from', params.from);
    if (params.to) qs.set('to', params.to);
    const suffix = qs.toString() ? `?${qs.toString()}` : '';
    return request(`/inbox/parse-counts${suffix}`);
  },

  listInbox: async (params: {
    status?: string;
    search?: string;
    page?: number;
    pageSize?: number;
    /** `parsing_status` to filter on. Not 'stalled' — see getParseCounts. */
    parse?: string;
    /** `received_at` bounds as `YYYY-MM-DD`, inclusive at both ends. */
    from?: string;
    to?: string;
  }): Promise<
    ApiResponse<InboxEmail[]> & { total?: number }
  > => {
    const qs = new URLSearchParams();
    if (params.status) qs.set('status', params.status);
    if (params.search) qs.set('search', params.search);
    if (params.parse) qs.set('parse', params.parse);
    if (params.from) qs.set('from', params.from);
    if (params.to) qs.set('to', params.to);
    qs.set('page', String(params.page ?? 1));
    qs.set('pageSize', String(params.pageSize ?? 20));

    try {
      const headers = await authHeaders();
      const res = await fetch(`${API_BASE}/inbox?${qs.toString()}`, { headers });
      const { json, parseError } = await parseJsonResponse(res);
      if (parseError) return { success: false, error: parseError };
      if (!res.ok || !json.success) return { success: false, error: json.error ?? `Request failed (${res.status})` };
      return { success: true, data: json.data as InboxEmail[], total: json.total as number };
    } catch (error: unknown) {
      return { success: false, error: (error as Error).message };
    }
  },

  getInboxItem: async (emailId: string): Promise<
    ApiResponse<Omit<InboxEmail, 'attachments'> & { bodyHtml: string | null; bodyText: string | null; attachments: InboxAttachment[] }>
  > => request(`/inbox/${emailId}`),

  fetchAttachmentBlob: async (emailId: string, attachmentId: string): Promise<ApiResponse<Blob>> => {
    try {
      const headers = await authHeaders();
      const res = await fetch(`${API_BASE}/inbox/${emailId}/attachments/${attachmentId}/download`, { headers });
      if (!res.ok) {
        const json = await res.json().catch(() => null);
        return { success: false, error: json?.error ?? `Request failed (${res.status})` };
      }
      return { success: true, data: await res.blob() };
    } catch (error: unknown) {
      return { success: false, error: (error as Error).message };
    }
  },

  assign: async (
    emailId: string,
    siteId: string,
    billId: string,
    retailerId?: string | null,
  ): Promise<ApiResponse<{ parsingAttachments: number; parseQueued?: boolean; warning?: string }>> =>
    request(`/inbox/${emailId}/assign`, {
      method: 'POST',
      body: JSON.stringify({
        site_id: siteId,
        bill_id: billId,
        // undefined = leave whatever retailer is on the bill; null = clear it.
        ...(retailerId !== undefined ? { retailer_id: retailerId } : {}),
      }),
    }),

  // Consolidated invoice: assign every resolved site in one call. The backend
  // creates/matches a bill per section and reports which resolved, which didn't.
  assignSections: async (
    emailId: string,
    sections: AssignSection[],
  ): Promise<ApiResponse<AssignSectionResult['data']>> =>
    request(`/inbox/${emailId}/assign`, {
      method: 'POST',
      body: JSON.stringify({ sections }),
    }),

  ignore: async (emailId: string): Promise<ApiResponse<null>> =>
    request(`/inbox/${emailId}/ignore`, { method: 'POST' }),

  /**
   * One email, several invoices: each becomes its own inbox item, matched to
   * its own NMI's site, assigned and parsed. Refused once the email is on a bill.
   */
  splitEmail: async (emailId: string): Promise<ApiResponse<{ added: number; reason: string; emailIgnored: boolean }>> =>
    request(`/inbox/${emailId}/split`, { method: 'POST' }),

  /**
   * Take one invoice off a bill period another invoice shares (a re-sent bill).
   * The email is kept, ignored and unassigned, so a later sync does not bring
   * it back onto the bill. Refused when it is the period's only invoice.
   */
  removeFromBill: async (emailId: string): Promise<ApiResponse<null>> =>
    request(`/inbox/${emailId}/remove-from-bill`, { method: 'POST' }),

  /**
   * Parse, or parse again, whatever this email carries.
   *
   * `sections` and `extraInstructions` are the re-parse dialog's narrowing
   * controls, and they used not to be sent at all: the email path took only the
   * retailer, so choosing "just the distribution charges" on an emailed invoice
   * replaced the whole parse and every "Edit parsed" correction with it.
   *
   * Resolves once the parse is HANDED OFF, not once it is done — watch
   * `parsing_status` for the outcome.
   */
  retryParse: async (
    emailId: string,
    retailerId?: string | null,
    options?: { sections?: ShadowBillSection[]; extraInstructions?: string },
  ): Promise<ApiResponse<{ retrying: number }>> =>
    request(`/inbox/${emailId}/retry-parse`, {
      method: 'POST',
      body: JSON.stringify({
        ...(retailerId !== undefined ? { retailer_id: retailerId } : {}),
        ...(options?.sections?.length ? { sections: options.sections } : {}),
        ...(options?.extraInstructions ? { extra_instructions: options.extraInstructions } : {}),
      }),
    }),

  /**
   * Re-parse the failed invoices, a few at a time.
   *
   * Takes the same `received_at` bounds as the list and the counts so the button
   * acts on exactly the invoices its number describes — omit them to retry every
   * failure in the organisation.
   *
   * Resolves once the work is QUEUED, not once it is done — the server parses a
   * few at a time and a full backlog runs for far longer than a request. Watch
   * the outcome through the parse counts, same as any other parse.
   *
   * `skipped` counts failures whose email was never assigned to a bill: there is
   * nothing for a parse to write into, so they are left alone rather than queued
   * to fail again.
   */
  retryAllFailed: async (params: { from?: string; to?: string } = {}): Promise<
    ApiResponse<{ retrying: number; skipped: number; concurrency: number }>
  > => {
    const qs = new URLSearchParams();
    if (params.from) qs.set('from', params.from);
    if (params.to) qs.set('to', params.to);
    const suffix = qs.toString() ? `?${qs.toString()}` : '';
    return request(`/inbox/retry-failed${suffix}`, { method: 'POST' });
  },

  /**
   * How many auto-matched emails bulk assign would attach to their site, and
   * how many it would leave for a person, by reason.
   */
  previewBulkAssign: async (): Promise<ApiResponse<{ assignable: number; skipped: Record<BulkAssignSkip, number> }>> =>
    request('/inbox/bulk-assign/preview'),

  /**
   * Assign the next batch of safe auto-matched emails to their site's bill
   * period. Call again while `remaining` is above zero, passing back every
   * `leftIds` seen so far. Parsing follows from the server's parse sweep.
   */
  bulkAssign: async (exclude: string[] = []): Promise<ApiResponse<{
    assigned: number;
    failed: number;
    periodTaken: number;
    /** Not assigned this call; send back as `exclude` on the next. */
    leftIds: string[];
    remaining: number;
    skipped: Record<BulkAssignSkip, number>;
  }>> => request('/inbox/bulk-assign', { method: 'POST', body: JSON.stringify({ exclude }) }),

  /**
   * Re-parse a hand-picked set of invoices, a few at a time.
   *
   * Like retryAllFailed, resolves when the work is QUEUED rather than finished.
   * Accepts anything not already parsed — a stalled or never-started row is a
   * legitimate thing to tick — so only successful parses are left alone.
   */
  retrySelected: async (
    emailIds: string[],
  ): Promise<ApiResponse<{ retrying: number; skipped: number; concurrency: number }>> =>
    request('/inbox/retry-selected', {
      method: 'POST',
      body: JSON.stringify({ email_ids: emailIds }),
    }),

  /**
   * Re-read ONE email's invoice with AI.
   *
   * An email's meter number and period are worked out once, when it arrives
   * from Gmail. Anything ingested before AI extraction existed still shows what
   * the old text matching found — including account and invoice numbers offered
   * as meters. Resolves when the re-read is DONE, so the drawer can show the
   * corrected values straight away.
   *
   * `pdfTextEmpty` means the invoice yielded no text at all (a scan) — nothing
   * was recoverable and re-running will not help.
   */
  reindex: async (
    emailId: string,
  ): Promise<ApiResponse<{
    emailId: string;
    detectedNmis: string[];
    pdfTextEmpty: boolean;
    /** The re-read found everything and the email is now assigned and parsing. */
    assigned: boolean;
    /** What is still missing when it could not finish. */
    missing: HeldReason[] | null;
    reason: string | null;
  }>> =>
    request(`/inbox/${emailId}/reindex`, { method: 'POST' }),

  /**
   * Re-read the inbox backlog with AI, a few at a time.
   *
   * Resolves when the work is QUEUED, not when it is finished — a few hundred
   * emails takes minutes. Defaults to only those the AI has never read, so
   * running it twice costs nothing.
   */
  reindexAll: async (
    opts: { includeAlreadyExtracted?: boolean; from?: string; to?: string; limit?: number } = {},
  ): Promise<ApiResponse<{ reindexing: number; concurrency: number; maxPerRun: number }>> =>
    request('/inbox/reindex-all', {
      method: 'POST',
      body: JSON.stringify({
        include_already_extracted: opts.includeAlreadyExtracted,
        from: opts.from,
        to: opts.to,
        limit: opts.limit,
      }),
    }),

  deleteEmail: async (emailId: string): Promise<ApiResponse<null>> =>
    request(`/inbox/${emailId}`, { method: 'DELETE' }),

  /**
   * Add one PDF to the inbox as if it had been emailed in — for bills that only
   * exist on a retailer's portal. Stored straight from the browser under the
   * org's `inbox-uploads/` folder (an API request body is too small for an
   * invoice), then ingested by the server from there.
   */
  uploadInvoice: async (file: File): Promise<ApiResponse<InboxUploadResult>> => {
    const invalid = validateInvoicePdf(file);
    if (invalid) return { success: false, error: invalid };
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return { success: false, error: 'Not signed in' };
      const { data: member } = await supabase
        .from('base_organisation_members')
        .select('organisation_id')
        .eq('user_id', user.id)
        .limit(1)
        .single();
      const orgId = member?.organisation_id as string | undefined;
      if (!orgId) return { success: false, error: 'Organization not found' };

      const safe = file.name.replace(/[^a-zA-Z0-9._-]/g, '_');
      const path = `${orgId}/inbox-uploads/${crypto.randomUUID()}/${safe}`;
      const { error: uploadError } = await supabase.storage
        .from('bv-attachments')
        .upload(path, file, { upsert: false, contentType: 'application/pdf' });
      if (uploadError) return { success: false, error: `Upload failed: ${uploadError.message}` };

      return request('/inbox/upload', {
        method: 'POST',
        body: JSON.stringify({ storage_path: path, filename: file.name }),
      });
    } catch (error: unknown) {
      return { success: false, error: (error as Error).message };
    }
  },

  // Live Gmail search — like Gmail's own search box, not restricted to attachments.
  // Read-only: nothing gets written to our DB until the user picks one via ingestSelected.
  searchSuggest: async (query: string): Promise<ApiResponse<EmailSuggestion[]>> =>
    request('/inbox/search-suggest', { method: 'POST', body: JSON.stringify({ query }) }),

  /**
   * Add exactly the previewed Gmail message to the inbox — nothing else. Pulling
   * in the rest of a site's history is the job of a sync over a chosen range.
   */
  ingestSelected: async (
    accountId: string,
    gmailMessageId: string,
  ): Promise<ApiResponse<{
    status: string;
    emailId?: string;
    /** Job 62: set when the message was already stored and its search index was rebuilt. */
    detectedNmis?: string[];
    /** The PDF carried no text layer, so its NMI cannot be made searchable. */
    pdfTextEmpty?: boolean;
  }>> =>
    request('/inbox/ingest-selected', {
      method: 'POST',
      body: JSON.stringify({ account_id: accountId, gmail_message_id: gmailMessageId }),
    }),

  previewGmail: async (accountId: string, gmailMessageId: string): Promise<ApiResponse<GmailPreview>> =>
    request('/inbox/preview-gmail', {
      method: 'POST',
      body: JSON.stringify({ account_id: accountId, gmail_message_id: gmailMessageId }),
    }),

  previewGmailAttachmentBlob: async (
    accountId: string,
    gmailMessageId: string,
    attachment: Pick<GmailPreviewAttachment, 'gmail_attachment_id' | 'filename' | 'mime_type'>,
  ): Promise<ApiResponse<Blob>> => {
    try {
      const headers = await authHeaders();
      const res = await fetch(`${API_BASE}/inbox/preview-gmail/attachment`, {
        method: 'POST',
        headers,
        body: JSON.stringify({
          account_id: accountId,
          gmail_message_id: gmailMessageId,
          gmail_attachment_id: attachment.gmail_attachment_id,
          filename: attachment.filename,
          mime_type: attachment.mime_type,
        }),
      });
      if (!res.ok) {
        const json = await res.json().catch(() => null);
        return { success: false, error: json?.error ?? `Request failed (${res.status})` };
      }
      return { success: true, data: await res.blob() };
    } catch (error: unknown) {
      return { success: false, error: (error as Error).message };
    }
  },
};
